/**
 * Dancer Contract Service
 * Manages tax and bank information for dancers with country-specific validation
 * Follows Service Layer Architecture with Result pattern and structured logging
 */
import type { SupabaseClient } from '@supabase/supabase-js';

import { Result } from '@kit/shared/result';
import { BaseService } from '@kit/shared/services';
import type { Database } from '@kit/supabase/database';

// Type definitions
type ContractDetails =
  Database['public']['Tables']['artist_contract_details']['Row'];
type CreateContractInput =
  Database['public']['Tables']['artist_contract_details']['Insert'];
type CountryRequirements =
  Database['public']['Tables']['country_contract_requirements']['Row'];

export interface SaveContractDetailsInput {
  countryCode: string;
  taxId?: string;
  taxDocumentPath?: string;
  registrationType?: 'sole_trader' | 'company' | 'other';
  registrationNumber?: string;
  vatNumber?: string;
  iban?: string;
  bic?: string;
  bankName?: string;
  bankAddress?: string;
  accountHolderName?: string;
  routingNumber?: string;
  accountNumber?: string;
  sortCode?: string;
  /**
   * The dancer's declared VAT regime. Tri-state on purpose:
   *   true  — invoices with VAT
   *   false — below the threshold, does not charge VAT
   *   null  — undeclared; the VAT engine keeps deriving from `vatNumber`
   *           presence (invoice-data-utils.ts:308-345)
   *
   * `false` and `null` are NOT interchangeable. See the mapper below.
   */
  vatRegistered?: boolean | null;
}

/**
 * Service class for handling dancer contract details operations
 * Provides type-safe operations with proper error handling and IBAN validation
 */
export class DancerContractService extends BaseService {
  constructor(client: SupabaseClient<Database>) {
    super(client);
  }

  /**
   * Get contract details for a user
   * Uses execute() for consistent error handling
   */
  async getContractDetails(
    userId: string,
  ): Promise<Result<ContractDetails | null>> {
    return this.execute(async () => {
      const context = { userId, operation: 'getContractDetails' };
      this.log('info', 'Getting contract details for user', context);

      const { data, error } = await this.client
        .from('artist_contract_details')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        this.log('error', 'Failed to get contract details', {
          ...context,
          error,
        });
        throw this.handleDatabaseError(error);
      }

      this.log('info', 'Contract details retrieved', {
        ...context,
        found: !!data,
      });

      return data;
    }, 'Failed to get contract details');
  }

  /**
   * Save or update contract details for a user
   * Creates new record or updates existing one
   * Uses execute() for consistent error handling
   */
  async saveContractDetails(
    userId: string,
    input: SaveContractDetailsInput,
  ): Promise<Result<ContractDetails>> {
    return this.execute(async () => {
      const context = { userId, operation: 'saveContractDetails' };
      this.log('info', 'Saving contract details', context);

      // Map input to database format
      const contractData: Omit<CreateContractInput, 'id' | 'created_at'> = {
        ...toContractRow(userId, input),
        updated_at: new Date().toISOString(),
      };

      // Upsert (insert or update)
      const { data, error } = await this.client
        .from('artist_contract_details')
        .upsert(contractData, {
          onConflict: 'user_id',
        })
        .select()
        .single();

      if (error) {
        this.log('error', 'Failed to save contract details', {
          ...context,
          error,
        });
        throw this.handleDatabaseError(error);
      }

      this.log('info', 'Contract details saved successfully', {
        ...context,
        detailsId: data.id,
      });

      return data;
    }, 'Failed to save contract details');
  }

  /**
   * Get country requirements by country code
   * Uses execute() for consistent error handling
   */
  async getCountryRequirements(
    countryCode: string,
  ): Promise<Result<CountryRequirements | null>> {
    return this.execute(async () => {
      const context = { countryCode, operation: 'getCountryRequirements' };
      this.log('info', 'Getting country requirements', context);

      const { data, error } = await this.client
        .from('country_contract_requirements')
        .select('*')
        .eq('country_code', countryCode)
        .maybeSingle();

      if (error) {
        this.log('error', 'Failed to get country requirements', {
          ...context,
          error,
        });
        throw this.handleDatabaseError(error);
      }

      this.log('info', 'Country requirements retrieved', {
        ...context,
        found: !!data,
      });

      return data;
    }, 'Failed to get country requirements');
  }

  /**
   * Get all supported countries and their requirements
   * Uses execute() for consistent error handling
   */
  async getAllCountryRequirements(): Promise<Result<CountryRequirements[]>> {
    return this.execute(async () => {
      const context = { operation: 'getAllCountryRequirements' };
      this.log('info', 'Getting all country requirements', context);

      const { data, error } = await this.client
        .from('country_contract_requirements')
        .select('*')
        .order('country_name');

      if (error) {
        this.log('error', 'Failed to get all country requirements', {
          ...context,
          error,
        });
        throw this.handleDatabaseError(error);
      }

      this.log('info', 'All country requirements retrieved', {
        ...context,
        count: data.length,
      });

      return data;
    }, 'Failed to get all country requirements');
  }

  /**
   * Validate IBAN format and checksum
   * Uses MOD 97 algorithm for checksum validation
   * Returns true if IBAN is valid, false otherwise
   */
  validateIBAN(iban: string): boolean {
    // Remove spaces and convert to uppercase
    const cleaned = iban.replace(/\s/g, '').toUpperCase();

    // Check length (varies by country, 15-34 chars)
    if (cleaned.length < 15 || cleaned.length > 34) {
      return false;
    }

    // Check format (2 letters + 2 digits + alphanumeric)
    if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]+$/.test(cleaned)) {
      return false;
    }

    // Move first 4 chars to end
    const rearranged = cleaned.slice(4) + cleaned.slice(0, 4);

    // Convert letters to numbers (A=10, B=11, etc.)
    const numeric = rearranged
      .split('')
      .map((c) => {
        const code = c.charCodeAt(0);
        return code >= 65 ? (code - 55).toString() : c;
      })
      .join('');

    // MOD 97 check using chunking for large numbers
    let remainder = numeric;
    while (remainder.length > 2) {
      const block = remainder.slice(0, 9);
      remainder = (parseInt(block, 10) % 97).toString() + remainder.slice(9);
    }

    return parseInt(remainder, 10) % 97 === 1;
  }

  /**
   * Validate tax ID against country-specific pattern
   * Returns true if tax ID matches pattern, false otherwise
   */
  async validateTaxId(
    taxId: string,
    countryCode: string,
  ): Promise<Result<boolean>> {
    return this.execute(async () => {
      const context = { countryCode, operation: 'validateTaxId' };
      this.log('info', 'Validating tax ID', context);

      // Get country requirements
      const requirementsResult = await this.getCountryRequirements(countryCode);
      if (!requirementsResult.isSuccess) {
        return false; // No requirements found, can't validate
      }

      const requirements = requirementsResult.getValue();
      if (!requirements || !requirements.tax_id_pattern) {
        return true; // No pattern defined, consider valid
      }

      // Test against regex pattern
      const pattern = new RegExp(requirements.tax_id_pattern);
      const isValid = pattern.test(taxId);

      this.log('info', 'Tax ID validation result', {
        ...context,
        isValid,
      });

      return isValid;
    }, 'Failed to validate tax ID');
  }
}

/**
 * The columns a dancer's own save writes, from the input it was given.
 *
 * Extracted so that anything needing to know WHICH fields a save would change
 * reads the same mapping the save applies, rather than re-deriving it. The
 * `|| null` on every string is load-bearing: '' and null are the same answer
 * for a tax id, so a comparison that skipped the fold would report a change
 * where there is none.
 *
 * `updated_at` is NOT included: it moves on every save by definition, and a
 * comparison including it would always report a difference.
 *
 * `vat_registered_since` is deliberately absent, which is what keeps a
 * dancer's save from clearing an admin-set date in this full-row upsert.
 *
 * Read "admin-only" as a property of the WRITE PATHS, not of the database. The
 * RLS on artist_contract_details is column-blind (baseline.sql:52800, and P7
 * in the WIP says so): a dancer who may update the row may update any column
 * of it. Only the fact that no dancer-facing code names this column keeps it
 * out of reach, so adding it to any dancer payload is enough to lose that.
 */
export function toContractRow(
  userId: string,
  input: SaveContractDetailsInput,
): Omit<CreateContractInput, 'id' | 'created_at' | 'updated_at'> {
  return {
    user_id: userId,
    country_code: input.countryCode,
    tax_id: input.taxId || null,
    tax_document_path: input.taxDocumentPath || null,
    registration_type: input.registrationType || null,
    registration_number: input.registrationNumber || null,
    vat_number: input.vatNumber || null,
    iban: input.iban || null,
    bic: input.bic || null,
    bank_name: input.bankName || null,
    bank_address: input.bankAddress || null,
    account_holder_name: input.accountHolderName || null,
    routing_number: input.routingNumber || null,
    account_number: input.accountNumber || null,
    sort_code: input.sortCode || null,
    // `??`, NOT `||`. Every column above is a string, where `|| null`
    // correctly folds '' into null. `vat_registered` is a tri-state boolean,
    // and `false || null` is `null`: that would silently turn "I am below the
    // threshold" into "I have not said", a different answer to the VAT engine.
    //
    // It still writes `null` when the caller OMITS the field, which erases a
    // declared value. Deliberate, and the same full-row contract every column
    // above obeys: this upsert writes the whole tax and bank block, and its
    // one caller sends every field it owns.
    //
    // Not "the whole block", which is what this said until 2026-08-23 and is
    // false: `taxDocumentPath` is declared on the input type and NO caller
    // sets it, so `tax_document_path` is permanently null and its
    // `|| null` above is a wipe with nothing to wipe. An input field with no
    // producer and a column with no reader is a YAGNI candidate, named here
    // rather than left for the next reader to rediscover.
    //
    // Why `vat_registered_since` is excluded on the SAME premise, which reads
    // like a contradiction and is not. The premise is "an absent column is
    // wiped"; the deciding question is OWNERSHIP, not the mechanic.
    // `vat_registered` belongs to this form, so the form may clear it.
    // `vat_registered_since` belongs to the admin, so this form must never be
    // able to clear it, and in a full-row upsert the only way to guarantee
    // that is to leave the column unnamed.
    vat_registered: input.vatRegistered ?? null,
  };
}

/**
 * True when a save would move the VAT regime and NOTHING else.
 *
 * The caller uses this to skip draft-invoice regeneration. `vat_registered` is
 * a DEFAULT that seeds new invoices; each invoice then carries its own
 * `invoices.vat_registered_override`. Regenerating on a regime change would
 * rewrite every existing draft from one click in the dancer's own profile,
 * with a service_role client and no admin step.
 *
 * Every other field still regenerates: a corrected IBAN or tax id genuinely
 * has to reach the drafts.
 *
 * Returns false when there is no previous row. A first save has no drafts to
 * protect, and regenerating is the safe direction.
 */
export function onlyVatRegimeChanged(
  previous: ContractDetails | null,
  input: SaveContractDetailsInput,
): boolean {
  if (!previous) return false;

  const next = toContractRow(previous.user_id, input);

  const changed = (Object.keys(next) as Array<keyof typeof next>).filter(
    (column) => next[column] !== previous[column],
  );

  return changed.length === 1 && changed[0] === 'vat_registered';
}
